/**
 * Spatial Filters dispatcher — Phase 4.E.filters.2.
 *
 * Exposes 9 destructive-commit filters that operate pre-kernel on
 * scene-linear ACEScg Float32 RGBA:
 *
 *   ┌──────────────┬──────────────────────────────────────────────────┐
 *   │ name         │ algorithm                                        │
 *   ├──────────────┼──────────────────────────────────────────────────┤
 *   │ gaussian     │ separable 1D Gaussian (reuses capture-sharpen)   │
 *   │ unsharpMask  │ orig + amount · (orig − blur) with threshold     │
 *   │ motionBlur   │ directional 1D Gaussian along (cosθ, sinθ)       │
 *   │ zoomBlur     │ radial Gaussian centred at (cx, cy)              │
 *   │ bilateral    │ Tomasi-Manduchi spatial × range Gaussians (K≤15) │
 *   │ median       │ Huang 1979 histogram-median for r > 3            │
 *   │ noise        │ xorshift32 Gaussian / uniform                    │
 *   │ emboss       │ Gx·cosθ + Gy·sinθ, gradient or flat mode         │
 *   │ edgeDetect   │ Sobel 3×3 magnitude                              │
 *   └──────────────┴──────────────────────────────────────────────────┘
 *
 * Every filter has the same shape:
 *
 *   defaultParams() → object
 *   isIdentityX(params) → boolean
 *   applyX(rgba, w, h, params) → rgba   (mutating in-place)
 *
 * The dispatcher's `apply(name, ...)` routes by name; the per-filter
 * modules are also exported for direct ESM imports (tests, future
 * non-UI callers).
 *
 * Pipeline-slot guidance: all spatial filters here are DESTRUCTIVE
 * pre-kernel commits, NOT per-frame slider passes. The UI flow is:
 *
 *   1. open filter dialog (modal with param sliders + small preview)
 *   2. user accepts → pushUndo() → snapshot()
 *   3. call applyX on E.srcFloat (in place)
 *   4. buildPreviewSource() to rebuild E.srcFloatPreview from new srcFloat
 *   5. queueRender() — the kernel sees the post-filter buffer next frame
 *
 * Cancel just returns; nothing committed.
 *
 * No worker integration — destructive edits run synchronously on the
 * main thread because they bypass the per-frame budget anyway. If the
 * user is on a 4K image and this takes 1-2 s, the modal can show a
 * spinner (Phase 4.E.filters.4 follow-up).
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted in any of the
 * per-filter modules. References cited in each file's header.
 *
 * @module src/pipeline/filters/spatial
 */

import * as gaussian    from './gaussian-blur.js';
import * as usm         from './unsharp-mask.js';
import * as motion      from './motion-blur.js';
import * as zoom        from './zoom-blur.js';
import * as bilateral   from './bilateral.js';
import * as median      from './median.js';
import * as noise       from './noise.js';
import * as emboss      from './emboss.js';
import * as edgeDetect  from './edge-detect.js';
import * as gpu         from './_gpu.js';

/* Wave 11.4 GPU helpers — re-exported so tests can probe / mock the
 * GPU availability flag without having to dynamic-import the
 * underscore-prefixed `_gpu.js` directly (some host environments
 * misroute underscore-prefixed paths under SPA-fallback). The
 * production filters do NOT use these re-exports; they import `_gpu.js`
 * directly. */
export const gpuHelpers = {
    isGPUAvailable: gpu.isGPUAvailable,
    /* Test hook — flip the GPU path off (or on) globally so the CPU
     * baseline can be measured against the GPU path on the same input.
     * Modules that have already captured the `isGPUAvailable` reference
     * (gaussian-blur.js et al.) see the new behaviour because the
     * function consults the shared `_forceDisabled` module flag. */
    setForceDisabled: gpu.setForceDisabled,
    _resetForTests:   gpu._resetForTests,
};
/* Re-export the box-approx helper for the GPU parity test's σ ≥ 50
 * routing checks. */
export { applyRepeatedBoxBlur, buildHalfKernel, BOX_THRESHOLD }
    from './gaussian-blur.js';

/* Re-export every filter so tests / direct callers can import by name. */
export { applyGaussianBlur, isIdentityGaussian }     from './gaussian-blur.js';
export { applyUnsharpMask,  isIdentityUSM }          from './unsharp-mask.js';
export { applyMotionBlur,   isIdentityMotion }       from './motion-blur.js';
export { applyZoomBlur,     isIdentityZoom }         from './zoom-blur.js';
export { applyBilateral,    isIdentityBilateral }    from './bilateral.js';
export { applyMedian,       isIdentityMedian }       from './median.js';
export { applyAddNoise,     isIdentityNoise }        from './noise.js';
export { applyEmboss,       isIdentityEmboss }       from './emboss.js';
export { applyEdgeDetect,   isIdentityEdgeDetect }   from './edge-detect.js';

/**
 * Canonical filter-name catalogue. Frozen so callers can rely on the
 * string set being stable across versions.
 */
export const SPATIAL_FILTER_NAMES = Object.freeze([
    'gaussian',
    'unsharpMask',
    'motionBlur',
    'zoomBlur',
    'bilateral',
    'median',
    'noise',
    'emboss',
    'edgeDetect',
]);

/**
 * Per-filter registry — exposes default params, identity test, and the
 * apply function under one name. Used by `apply()` and `defaultParams()`
 * below. Kept as a frozen lookup so external code can't mutate it.
 */
export const FILTERS = Object.freeze({
    gaussian: {
        apply:        gaussian.applyGaussianBlur,
        isIdentity:   gaussian.isIdentityGaussian,
        defaultParams: gaussian.defaultParams,
    },
    unsharpMask: {
        apply:        usm.applyUnsharpMask,
        isIdentity:   usm.isIdentityUSM,
        defaultParams: usm.defaultParams,
    },
    motionBlur: {
        apply:        motion.applyMotionBlur,
        isIdentity:   motion.isIdentityMotion,
        defaultParams: motion.defaultParams,
    },
    zoomBlur: {
        apply:        zoom.applyZoomBlur,
        isIdentity:   zoom.isIdentityZoom,
        defaultParams: zoom.defaultParams,
    },
    bilateral: {
        apply:        bilateral.applyBilateral,
        isIdentity:   bilateral.isIdentityBilateral,
        defaultParams: bilateral.defaultParams,
    },
    median: {
        apply:        median.applyMedian,
        isIdentity:   median.isIdentityMedian,
        defaultParams: median.defaultParams,
    },
    noise: {
        apply:        noise.applyAddNoise,
        isIdentity:   noise.isIdentityNoise,
        defaultParams: noise.defaultParams,
    },
    emboss: {
        apply:        emboss.applyEmboss,
        isIdentity:   emboss.isIdentityEmboss,
        defaultParams: emboss.defaultParams,
    },
    edgeDetect: {
        apply:        edgeDetect.applyEdgeDetect,
        isIdentity:   edgeDetect.isIdentityEdgeDetect,
        defaultParams: edgeDetect.defaultParams,
    },
});

/**
 * Dispatcher entry-point: route by name. Throws on unknown name so the
 * UI doesn't silently no-op a typo.
 *
 * The identity short-circuit is checked INSIDE each per-filter apply()
 * — calling `apply('gaussian', rgba, w, h, {sigma: 0})` is a no-op.
 *
 * @param {string} name             one of SPATIAL_FILTER_NAMES
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @returns {Float32Array}          same `rgba` reference (in-place)
 */
export function apply(name, rgba, w, h, params) {
    const entry = FILTERS[name];
    if (!entry) {
        throw new Error(`BlrrdFilters.spatial.apply: unknown filter "${name}"`);
    }
    return entry.apply(rgba, w, h, params);
}

/**
 * Return the default-params object for a given filter. Used by the UI
 * to seed the modal sliders before the user touches anything.
 *
 * @param {string} name
 * @returns {object}
 */
export function defaultParamsFor(name) {
    const entry = FILTERS[name];
    if (!entry) {
        throw new Error(`BlrrdFilters.spatial.defaultParamsFor: unknown filter "${name}"`);
    }
    return entry.defaultParams();
}

/**
 * Test whether the given params are an identity short-circuit for the
 * given filter. Lets the UI grey-out the OK button when sliders are at
 * defaults.
 *
 * @param {string} name
 * @param {object} params
 * @returns {boolean}
 */
export function isIdentityFor(name, params) {
    const entry = FILTERS[name];
    if (!entry) {
        throw new Error(`BlrrdFilters.spatial.isIdentityFor: unknown filter "${name}"`);
    }
    return entry.isIdentity(params);
}
